import { ID, LifeArea, Locales, LocalizedLifeArea } from '@griply/types';
import { resolveFavorites } from '@griply/utils';
import { useDeferredValue, useMemo } from 'react';
import { useGoalsQuery } from 'shared/hooks/queries/use-goals-query';
import { useLifeAreasQuery } from 'shared/hooks/queries/use-life-areas-query';
import { useTagsQuery } from 'shared/hooks/queries/use-tags-query';
import { useTasksQuery } from 'shared/hooks/queries/use-tasks-query';
import { useUser } from 'shared/hooks/use-user';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { FavoriteType } from 'shared/types/user-settings';
import { getLocalizedStringValue } from 'shared/utils/get-localized-string-value';

const processLocalizedLifeArea: (
  lifeArea: LifeArea | LocalizedLifeArea,
  locale: Locales,
) => LifeArea = (lifeArea, locale) => ({
  ...lifeArea,
  name: getLocalizedStringValue(lifeArea.name, locale),
});

export const useFavorites = () => {
  const user = useUser();
  const locale = useI18nLocale();
  const favorites = useMemo(
    () => user?.settings?.favorites ?? [],
    [user?.settings?.favorites],
  );

  const favoriteIds = useMemo(
    () =>
      favorites.reduce<Record<FavoriteType, ID[]>>(
        (acc, { type, id }) => {
          acc[type].push(id);
          return acc;
        },
        {
          [FavoriteType.Goal]: [],
          [FavoriteType.Task]: [],
          [FavoriteType.LifeArea]: [],
          [FavoriteType.Tag]: [],
        },
      ),
    [favorites],
  );

  const deferredFavoriteIds = useDeferredValue(favoriteIds);

  const { data: favoriteGoals } = useGoalsQuery(user?.uid, {
    ids: deferredFavoriteIds[FavoriteType.Goal],
  });
  const { data: favoriteLifeAreas } = useLifeAreasQuery(user?.uid, {
    ids: deferredFavoriteIds[FavoriteType.LifeArea],
  });
  const favoriteLocalizedLifeAreas = useMemo(
    () =>
      favoriteLifeAreas?.map((lifeArea) =>
        processLocalizedLifeArea(lifeArea, locale),
      ),
    [favoriteLifeAreas, locale],
  );

  const { data: favoriteTasks } = useTasksQuery(user?.uid, {
    ids: deferredFavoriteIds[FavoriteType.Task],
  });
  // get all tags for consistency over tags
  const { data: tags } = useTagsQuery(user?.uid);

  return useMemo(() => {
    const resolved = resolveFavorites({
      favoriteRefs: favorites,
      goals: favoriteGoals ?? [],
      tasks: favoriteTasks ?? [],
      lifeAreas: favoriteLocalizedLifeAreas ?? [],
      tags: tags ?? [],
    });

    return resolved.map((favorite) => {
      if (favorite.type !== FavoriteType.LifeArea) {
        return favorite;
      }

      return {
        ...favorite,
        value: {
          ...favorite.value,
          name: getLocalizedStringValue(favorite.value.name, locale),
        },
      };
    });
  }, [
    favorites,
    favoriteGoals,
    favoriteTasks,
    favoriteLocalizedLifeAreas,
    tags,
    locale,
  ]);
};
